import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { documentTaxPreviewEndpoints } from '@/lib/api/endpoints';
import { taxPolicyKeys } from '@/lib/queryKeys';
import type { SimulateLineCategory, SimulateRequest } from '@/hooks/taxes/useSimulateTax';
import type { Estimate } from '@/types/estimate';
import type { Invoice } from '@/types/invoice';

// ─── Wire shape (mirrors `document-tax-preview.service.ts` in operations) ────

export type DocumentTaxPreviewKind = 'estimate' | 'invoice';

/** What the calculator cannot reproduce about this document. */
export type DocumentTaxPreviewWarning =
  | 'no-address'
  | 'canadian-postal'
  | 'manual-tax-group'
  | 'reprices-differently';

export type ExcludedLineReason =
  | 'not-selected'
  | 'declined'
  | 'bundled'
  | 'membership'
  | 'not-taxable';

export interface DocumentTaxPreviewInput {
  kind: DocumentTaxPreviewKind;
  id: string;
  number: string;
  title: string | null;
  customerName: string | null;
  status: string;
  context: {
    zipCode: string | null;
    stateCode: string | null;
    businessUnitId: string | null;
    jobTypeId: string | null;
    contactId: string | null;
    segment: 'RESIDENTIAL' | 'COMMERCIAL' | null;
  };
  /**
   * One line per base the document's pricing taxes — discounts applied and
   * rolled-up bundles folded — so each is simulated at quantity 1.
   */
  lineItems: Array<{
    category: SimulateLineCategory;
    description: string;
    amount: number;
    quantity: 1;
    lineCount: number;
  }>;
  /** Lines that owe no tax of their own; `amount` is what they add untaxed. */
  excluded: Array<{
    description: string;
    reason: ExcludedLineReason;
    amount: number;
    /** The base it would be taxed as, were it taxable. */
    category: SimulateLineCategory;
    quantity: number;
  }>;
  current: {
    subtotal: number;
    taxTotal: number;
    total: number;
    taxResolvedFrom: string | null;
  };
  warnings: DocumentTaxPreviewWarning[];
}

/**
 * GET /{estimates|invoices}/:id/tax-preview-input — a stored document as the
 * tax calculator's input. Read-only; loading one changes nothing on it.
 */
export function useDocumentTaxPreviewInput(
  doc: { kind: DocumentTaxPreviewKind; id: string } | null,
) {
  return useApiQuery<DocumentTaxPreviewInput>({
    queryKey: taxPolicyKeys.documentInput(doc?.kind ?? '', doc?.id ?? ''),
    queryFn: () =>
      apiGet<DocumentTaxPreviewInput>(
        operationsClient,
        documentTaxPreviewEndpoints[doc!.kind](doc!.id),
      ),
    enabled: doc !== null,
    // The comparison is against the document as it is NOW; a stale copy would
    // compare the rule against totals the document no longer has.
    staleTime: 0,
    showErrorToast: false,
  });
}

/** What the calculator is trying: the axes it may have moved, and the draft rule. */
export interface EstimateTaxPreviewRequest {
  zipCode?: string;
  stateCode?: string;
  businessUnitId?: string | null;
  contactId?: string | null;
  jobTypeId?: string | null;
  draftPolicy?: SimulateRequest['draftPolicy'];
}

/** The estimate's lines and totals, repriced — merged over the estimate itself. */
export type EstimateTaxPreview = Pick<
  Estimate,
  | 'lineItems'
  | 'subtotal'
  | 'discountTotal'
  | 'membershipDiscountTotal'
  | 'taxTotal'
  | 'grandTotal'
  | 'depositAmount'
>;

/**
 * POST /estimates/:id/tax-preview — the estimate repriced through its own tax
 * path with the rule being tried. Nothing on the estimate is written.
 */
export function useEstimateTaxPreview() {
  return useApiMutation<EstimateTaxPreview, { id: string; body: EstimateTaxPreviewRequest }>({
    mutationFn: ({ id, body }) =>
      apiPost<EstimateTaxPreview>(
        operationsClient,
        documentTaxPreviewEndpoints.estimateRepriced(id),
        body,
      ),
    showErrorToast: false,
  });
}

/** The invoice's lines and totals, repriced — merged over the invoice itself. */
export type InvoiceTaxPreview = Pick<
  Invoice,
  | 'lineItems'
  | 'subtotal'
  | 'discountTotal'
  | 'membershipDiscountTotal'
  | 'taxTotal'
  | 'total'
  | 'balanceDue'
>;

/**
 * POST /invoices/:id/tax-preview — the invoice repriced through its own pricing
 * path with the rule being tried. Nothing on the invoice is written.
 */
export function useInvoiceTaxPreview() {
  return useApiMutation<InvoiceTaxPreview, { id: string; body: EstimateTaxPreviewRequest }>({
    mutationFn: ({ id, body }) =>
      apiPost<InvoiceTaxPreview>(
        operationsClient,
        documentTaxPreviewEndpoints.invoiceRepriced(id),
        body,
      ),
    showErrorToast: false,
  });
}
